import { Injectable } from '@angular/core';
import { Plugin, registerPlugin } from '@capacitor/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { SharedPluginListener } from 'src/app/helpers/hardware/shared-plugin-listener.helper';
import { HardwareDeviceSelectors } from 'src/app/store/hardware-device/hardware-device.selector';

export interface NFCReaderPlugin extends Plugin {
  startNFC(): Promise<{ value: string }>;

  stopNFC(): Promise<{ value: string }>;

  isNFCEnabled(): Promise<{ success: boolean }>;
}

interface NFCDataI {
  data: string;
}

@Injectable({
  providedIn: 'root'
})
export class NfcReaderService {
  private NFCReader: NFCReaderPlugin;
  private readonly listener: SharedPluginListener<NFCDataI>;

  constructor(private readonly _store: Store) {
    this.NFCReader = registerPlugin<NFCReaderPlugin>('CloudStormNFC');
    this.listener = new SharedPluginListener<NFCDataI>({
      tag: '[NfcReaderService]',
      registerListeners: (emit) =>
        this.NFCReader.addListener('NFCReaderData', emit),
      start: () => this.NFCReader.startNFC(),
      stop: () => this.NFCReader.stopNFC()
    });
  }

  /**
   * Start receiving NFC tags. Reader mode stays enabled while at least one subscription is open;
   * unsubscribe the returned Subscription when the consumer is destroyed.
   */
  startNFC(callback: (payload: NFCDataI) => void) {
    if (this._isAndroid && this._isNfcEnabled) {
      return this.listener.subscribe(callback);
    }

    return new Subscription();
  }

  async isNFCEnabled(): Promise<{ success: boolean }> {
    return await this.NFCReader.isNFCEnabled();
  }

  private get _isAndroid() {
    return this._store.selectSnapshot(HardwareDeviceSelectors.isAndroid);
  }

  private get _isNfcEnabled() {
    return this._store.selectSnapshot(HardwareDeviceSelectors.nfcEnabled);
  }
}
